import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtMoney } from "@/lib/api";
import { useMasters, opt, optIncludingInactive } from "@/lib/useMasters";
import { PageHeader, Card, Field, SelectInput, TextInput, NumberInput, StatusBadge, CrudActions } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, Wallet, Plus, Minus, Printer, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DENOMINATIONS = [500, 200, 100, 50, 20, 10, 5, 2, 1];

const TYPE_LABEL = {
  CASH_RECEIVED: "Cash Received",
  LABOUR_PAYMENT: "Labour Payment",
  DALA_SHORTFALL: "Dala Shortfall",
  SRM_EXPENSE: "SRM Expense",
  PARTY_RECOVERABLE: "Party Recoverable",
  CASH_ADJUSTMENT: "Cash Adjustment",
};

export default function ManagerCash() {
  const m = useMasters();
  const qc = useQueryClient();
  const { user } = useAuth();
  const warehouses = m.warehouses || [];
  const [siteFilter, setSiteFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const { data: balance, isLoading: balLoading } = useQuery({
    queryKey: ["cash-balance"],
    queryFn: async () => (await api.get("/cash/balance")).data,
  });

  const { data: statement, isLoading: stmtLoading } = useQuery({
    queryKey: ["cash-statement", siteFilter, dateFrom, dateTo],
    queryFn: async () => (await api.get("/cash/statement", {
      params: {
        ...(siteFilter ? { warehouse_id: siteFilter } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      },
    })).data,
  });

  const { data: recons = [] } = useQuery({
    queryKey: ["cash-recons"],
    queryFn: async () => (await api.get("/cash/reconciliations")).data,
  });

  const canApprove = ["Super Admin", "Management / Owner", "Accountant"].includes(user?.role);

  const removeEntry = async (r) => {
    if (!window.confirm(`Delete ${r.voucher_number}? Party/labour ledgers linked to this voucher will be reversed.`)) return;
    try {
      await api.delete(`/cash/entries/${r.id}`);
      toast.success(`${r.voucher_number} deleted`);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/cash/reconcile/${id}/approve`);
      toast.success("Reconciliation approved");
      qc.invalidateQueries({ queryKey: ["cash-recons"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["cash-balance"] });
    qc.invalidateQueries({ queryKey: ["cash-statement"] });
    qc.invalidateQueries({ queryKey: ["cash-recons"] });
  };

  return (
    <div data-testid="manager-cash-page">
      <PageHeader
        title="Manager Cash Ledger"
        subtitle="One shared cash book for all warehouses — top-ups, payouts, and a single daily closing"
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Filter by site (optional)" className="min-w-[220px]">
          <SelectInput
            options={[{ value: "", label: "All warehouses (full book)" }, ...opt(warehouses, (w) => w.name)]}
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            data-testid="cash-site-filter"
          />
        </Field>
        <Field label="From"><TextInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Field>
        <Field label="To"><TextInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Field>
      </div>

      <Card className="mb-6 overflow-hidden border-emerald-200 bg-gradient-to-br from-emerald-800 to-emerald-900 p-6 text-white" data-testid="cash-balance-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-white/15">
              <Wallet size={24} />
            </div>
            <div>
              <div className="text-sm font-medium text-emerald-100/90">Current Cash in Hand</div>
              <div className="mt-1 font-display text-4xl font-extrabold tracking-tight" data-testid="cash-balance-value">
                {balLoading ? "…" : fmtMoney(balance?.available_cash_balance ?? 0)}
              </div>
              <div className="mt-1 text-xs text-emerald-200">
                Shared across {balance?.warehouse_count ?? "all"} warehouses · one manager cash pot
              </div>
              {balance?.today_reconciled && (
                <div className="mt-1 text-xs text-emerald-200">
                  Today closed · {balance.today_reconciliation_status}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="cash-receive-btn"
              onClick={() => setReceiveOpen(true)}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold text-emerald-900 hover:bg-emerald-50"
            >
              <Plus size={16} /> Receive Cash
            </button>
            <button
              data-testid="cash-pay-btn"
              onClick={() => setPayOpen(true)}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 text-sm font-semibold text-white hover:bg-white/20"
            >
              <Minus size={16} /> Pay Labour / Expense
            </button>
            <button
              data-testid="cash-close-btn"
              onClick={() => setCloseOpen(true)}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-amber-300/50 bg-amber-400/20 px-4 text-sm font-semibold text-amber-50 hover:bg-amber-400/30"
            >
              <CheckCircle2 size={16} /> Daily Closing
            </button>
          </div>
        </div>
      </Card>

      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-stone-800">Cash Register</h3>
        <button onClick={() => window.print()} className="print:hidden inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800">
          <Printer size={14} /> Print
        </button>
      </div>

      <Card className="overflow-hidden">
        {stmtLoading ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cash-register-table">
              <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Voucher</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Site</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3 text-right">Inflow</th>
                  <th className="px-4 py-3 text-right">Outflow</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3 text-right print:hidden">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!(statement?.entries || []).length && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-stone-400">No cash movements yet. Receive a top-up to start.</td></tr>
                )}
                {(statement?.entries || []).map((r) => (
                  <tr key={r.id} className="border-t border-stone-100 even:bg-stone-50/50">
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap">{(r.entry_date || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold text-emerald-700">{r.voucher_number}</td>
                    <td className="px-4 py-2.5 text-xs">{TYPE_LABEL[r.transaction_type] || r.transaction_type}</td>
                    <td className="px-4 py-2.5 text-xs text-stone-500">{r.warehouse_name || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-stone-600 max-w-xs">
                      {r.remarks || "—"}
                      {r.payee_name && <div className="text-stone-400">Payee: {r.payee_name}</div>}
                      {r.charged_to_name && <div className="text-stone-400">Charged to: {r.charged_to_name}</div>}
                      {r.received_from_name && <div className="text-stone-400">From: {r.received_from_name}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-right text-emerald-700">{r.inflow ? fmtMoney(r.inflow) : "—"}</td>
                    <td className="px-4 py-2.5 text-right text-rose-700">{r.outflow ? fmtMoney(r.outflow) : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{fmtMoney(r.running_cash_balance)}</td>
                    <td className="px-4 py-2.5 print:hidden">
                      <CrudActions
                        testid={`cash-${r.id}`}
                        onEdit={() => setEditEntry(r)}
                        onDelete={() => removeEntry(r)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              {(statement?.entries || []).length > 0 && (
                <tfoot className="border-t border-stone-200 bg-stone-50 font-semibold">
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-right text-xs uppercase text-stone-500">Totals</td>
                    <td className="px-4 py-3 text-right text-emerald-700">{fmtMoney(statement.total_inflow)}</td>
                    <td className="px-4 py-3 text-right text-rose-700">{fmtMoney(statement.total_outflow)}</td>
                    <td className="px-4 py-3 text-right">{fmtMoney(statement.available_cash_balance)}</td>
                    <td className="print:hidden" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      {recons.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 font-display text-lg font-semibold text-stone-800">Daily Closings</h3>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3 text-right">System</th>
                  <th className="px-4 py-3 text-right">Physical</th>
                  <th className="px-4 py-3 text-right">Diff</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {recons.map((r) => (
                  <tr key={r.id} className="border-t border-stone-100">
                    <td className="px-4 py-2.5">{r.date}</td>
                    <td className="px-4 py-2.5 text-right">{fmtMoney(r.system_cash_balance)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtMoney(r.physical_cash_count)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtMoney(r.discrepancy_amount)}</td>
                    <td className="px-4 py-2.5">
                      <StatusBadge tone={r.status === "MATCHED" || r.status === "APPROVED" ? "emerald" : "warning"}>
                        {r.status}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {canApprove && r.status !== "APPROVED" && r.status !== "MATCHED" && (
                        <button onClick={() => approve(r.id)} className="text-xs font-semibold text-emerald-800 hover:underline">
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      <ReceiveDialog open={receiveOpen} onClose={() => setReceiveOpen(false)} onSaved={refresh} />
      <PayDialog open={payOpen} balance={balance?.available_cash_balance ?? 0} onClose={() => setPayOpen(false)} onSaved={refresh} />
      <ClosingDialog open={closeOpen} onClose={() => setCloseOpen(false)} onSaved={refresh} />
      <EditCashDialog entry={editEntry} onClose={() => setEditEntry(null)} onSaved={refresh} />
    </div>
  );
}

function EditCashDialog({ entry, onClose, onSaved }) {
  const m = useMasters();
  const isReceipt = entry?.transaction_type === "CASH_RECEIVED";
  const isLabour = entry?.transaction_type === "LABOUR_PAYMENT" || entry?.transaction_type === "DALA_SHORTFALL";
  const isParty = entry?.transaction_type === "PARTY_RECOVERABLE";
  const [f, setF] = useState({
    amount: "",
    warehouse_id: "",
    received_from_party_id: "",
    payee_party_id: "",
    charged_to_party_id: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setF({
      amount: entry.amount != null ? String(entry.amount) : "",
      warehouse_id: entry.untagged_site ? "" : (entry.warehouse_id ? String(entry.warehouse_id) : ""),
      received_from_party_id: entry.received_from_party_id ? String(entry.received_from_party_id) : "",
      payee_party_id: entry.payee_party_id ? String(entry.payee_party_id) : "",
      charged_to_party_id: entry.charged_to_party_id ? String(entry.charged_to_party_id) : "",
      remarks: entry.remarks || "",
    });
  }, [entry]);

  const save = async () => {
    if (!(+f.amount > 0)) return toast.error("Enter a positive amount");
    setSaving(true);
    try {
      const body = {
        amount: +f.amount,
        warehouse_id: f.warehouse_id ? +f.warehouse_id : null,
        remarks: f.remarks || null,
      };
      if (isReceipt) {
        body.received_from_party_id = f.received_from_party_id ? +f.received_from_party_id : null;
      } else {
        body.payee_party_id = f.payee_party_id ? +f.payee_party_id : null;
        body.charged_to_party_id = f.charged_to_party_id ? +f.charged_to_party_id : null;
      }
      await api.put(`/cash/entries/${entry.id}`, body);
      toast.success(
        isReceipt && f.received_from_party_id
          ? "Cash entry updated — party ledger payment posted"
          : "Cash entry updated",
      );
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cash-edit-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">
            Edit {entry?.voucher_number} · {TYPE_LABEL[entry?.transaction_type] || entry?.transaction_type}
          </DialogTitle>
        </DialogHeader>
        {isReceipt && (
          <p className="text-sm text-stone-500">
            Set <strong>Received from</strong> to BTC (or any party) to post a payment credit on their ledger.
          </p>
        )}
        <div className="grid gap-3">
          <Field label="Amount (₹)" required>
            <NumberInput value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="edit-cash-amount" />
          </Field>
          <Field label="Site tag (optional)">
            <SelectInput
              options={[{ value: "", label: "Not tagged" }, ...opt(m.warehouses || [], (w) => w.name)]}
              value={f.warehouse_id}
              onChange={(e) => setF({ ...f, warehouse_id: e.target.value })}
            />
          </Field>
          {isReceipt && (
            <Field label="Received from (party)">
              <SelectInput
                options={[{ value: "", label: "SRM top-up (no party ledger)" }, ...optIncludingInactive(m.parties, (p) => `${p.name} (${p.code})`)]}
                value={f.received_from_party_id}
                onChange={(e) => setF({ ...f, received_from_party_id: e.target.value })}
                data-testid="edit-cash-from"
              />
            </Field>
          )}
          {isLabour && (
            <Field label="Labour contractor / payee" required={entry?.transaction_type === "LABOUR_PAYMENT"}>
              <SelectInput
                options={optIncludingInactive(m.parties, (p) => `${p.name} (${p.code})`)}
                value={f.payee_party_id}
                onChange={(e) => setF({ ...f, payee_party_id: e.target.value })}
              />
            </Field>
          )}
          {(isLabour || isParty) && (
            <Field label={isParty ? "Charged to party" : "Also recover from (optional)"}>
              <SelectInput
                options={[{ value: "", label: "—" }, ...optIncludingInactive(m.parties, (p) => `${p.name} (${p.code})`)]}
                value={f.charged_to_party_id}
                onChange={(e) => setF({ ...f, charged_to_party_id: e.target.value })}
              />
            </Field>
          )}
          <Field label="Remarks">
            <TextInput value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </Field>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-edit-cash-btn" className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={18} /> : null} Save changes
        </button>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ open, onClose, onSaved }) {
  const m = useMasters();
  const [f, setF] = useState({ amount: "", received_from_party_id: "", warehouse_id: "", remarks: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF({ amount: "", received_from_party_id: "", warehouse_id: "", remarks: "" });
  }, [open]);

  const save = async () => {
    if (!(+f.amount > 0)) return toast.error("Enter a positive amount");
    setSaving(true);
    try {
      await api.post("/cash/receipt", {
        amount: +f.amount,
        warehouse_id: f.warehouse_id ? +f.warehouse_id : null,
        received_from_party_id: f.received_from_party_id ? +f.received_from_party_id : null,
        remarks: f.remarks || undefined,
      });
      toast.success("Cash top-up recorded");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cash-receive-dialog">
        <DialogHeader><DialogTitle className="font-display">Receive Cash (Top-Up)</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">Adds to the shared manager cash pot (all warehouses).</p>
        <div className="grid gap-3">
          <Field label="Amount (₹)" required>
            <NumberInput value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="receive-amount" />
          </Field>
          <Field label="Site tag (optional)">
            <SelectInput
              options={[{ value: "", label: "Not tagged" }, ...opt(m.warehouses || [], (w) => w.name)]}
              value={f.warehouse_id}
              onChange={(e) => setF({ ...f, warehouse_id: e.target.value })}
            />
          </Field>
          <Field label="Received from (optional)">
            <SelectInput
              options={optIncludingInactive(m.parties, (p) => `${p.name} (${p.code})`)}
              value={f.received_from_party_id}
              onChange={(e) => setF({ ...f, received_from_party_id: e.target.value })}
            />
          </Field>
          <p className="text-xs text-stone-500 -mt-1">
            If you select a party, their ledger is credited (payment received). Leave blank for SRM owner top-up only.
          </p>
          <Field label="Remarks">
            <TextInput value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </Field>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-receive-btn" className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} Save Receipt
        </button>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ open, balance, onClose, onSaved }) {
  const m = useMasters();
  const { data: expenseTypes = [] } = useQuery({
    queryKey: ["cash-expense-types"],
    queryFn: async () => (await api.get("/cash/expense-types")).data,
    enabled: open,
  });
  const [f, setF] = useState({
    transaction_type: "LABOUR_PAYMENT",
    amount: "",
    warehouse_id: "",
    payee_party_id: "",
    charged_to_party_id: "",
    expense_type_id: "",
    remarks: "",
    source_transaction_ref: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setF({
        transaction_type: "LABOUR_PAYMENT",
        amount: "",
        warehouse_id: "",
        payee_party_id: "",
        charged_to_party_id: "",
        expense_type_id: "",
        remarks: "",
        source_transaction_ref: "",
      });
    }
  }, [open]);

  const save = async () => {
    if (!(+f.amount > 0)) return toast.error("Enter a positive amount");
    if (+f.amount > +balance) return toast.error(`Exceeds cash in hand (${fmtMoney(balance)})`);
    setSaving(true);
    try {
      await api.post("/cash/disbursement", {
        amount: +f.amount,
        warehouse_id: f.warehouse_id ? +f.warehouse_id : null,
        transaction_type: f.transaction_type,
        payee_party_id: f.payee_party_id ? +f.payee_party_id : null,
        charged_to_party_id: f.charged_to_party_id ? +f.charged_to_party_id : null,
        expense_type_id: f.expense_type_id ? +f.expense_type_id : null,
        remarks: f.remarks || undefined,
        source_transaction_ref: f.source_transaction_ref || undefined,
      });
      toast.success("Cash payment recorded");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const tx = f.transaction_type;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cash-pay-dialog" className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Pay from Manager Cash</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-600">
          Shared cash available: <span className="font-semibold text-stone-900">{fmtMoney(balance)}</span>
        </div>
        <div className="grid gap-3">
          <Field label="Category" required>
            <SelectInput
              options={[
                { value: "LABOUR_PAYMENT", label: "Labour Payout" },
                { value: "DALA_SHORTFALL", label: "Dala Shortfall" },
                { value: "SRM_EXPENSE", label: "Operational Overhead (SRM)" },
                { value: "PARTY_RECOVERABLE", label: "Party Recoverable Expense" },
              ]}
              value={f.transaction_type}
              onChange={(e) => setF({ ...f, transaction_type: e.target.value })}
              data-testid="pay-type"
            />
          </Field>
          <Field label="Amount (₹)" required>
            <NumberInput value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="pay-amount" />
          </Field>
          <Field label="Site where spent (optional)">
            <SelectInput
              options={[{ value: "", label: "Not tagged" }, ...opt(m.warehouses || [], (w) => w.name)]}
              value={f.warehouse_id}
              onChange={(e) => setF({ ...f, warehouse_id: e.target.value })}
            />
          </Field>
          {(tx === "LABOUR_PAYMENT" || tx === "DALA_SHORTFALL") && (
            <Field label={tx === "LABOUR_PAYMENT" ? "Labour contractor / payee" : "Paid to (optional)"} required={tx === "LABOUR_PAYMENT"}>
              <SelectInput
                options={optIncludingInactive(m.parties, (p) => `${p.name} (${p.code})`)}
                value={f.payee_party_id}
                onChange={(e) => setF({ ...f, payee_party_id: e.target.value })}
                data-testid="pay-payee"
              />
            </Field>
          )}
          {(tx === "LABOUR_PAYMENT" || tx === "DALA_SHORTFALL" || tx === "PARTY_RECOVERABLE") && (
            <Field label={tx === "PARTY_RECOVERABLE" ? "Charged to party" : "Also recover from party (optional)"} required={tx === "PARTY_RECOVERABLE"}>
              <SelectInput
                options={optIncludingInactive(m.parties, (p) => `${p.name} (${p.code})`)}
                value={f.charged_to_party_id}
                onChange={(e) => setF({ ...f, charged_to_party_id: e.target.value })}
              />
            </Field>
          )}
          {(tx === "LABOUR_PAYMENT" || tx === "DALA_SHORTFALL") && (
            <p className="text-xs text-stone-500 -mt-1">
              Contractor ledger is always updated. Leave “recover from” blank if labour/dala was already charged on inward/outward.
            </p>
          )}
          {tx === "SRM_EXPENSE" && (
            <Field label="Expense type" required>
              <SelectInput
                options={(expenseTypes || []).map((t) => ({ value: t.id, label: t.name }))}
                value={f.expense_type_id}
                onChange={(e) => setF({ ...f, expense_type_id: e.target.value })}
                data-testid="pay-expense-type"
              />
            </Field>
          )}
          <Field label="Link to txn (optional)">
            <TextInput placeholder="Inward / Outward / Settlement no." value={f.source_transaction_ref} onChange={(e) => setF({ ...f, source_transaction_ref: e.target.value })} />
          </Field>
          <Field label="Remarks">
            <TextInput value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </Field>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-pay-btn" className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Minus size={18} />} Save Payment
        </button>
      </DialogContent>
    </Dialog>
  );
}

function ClosingDialog({ open, onClose, onSaved }) {
  const [preview, setPreview] = useState(null);
  const [counts, setCounts] = useState(() => Object.fromEntries(DENOMINATIONS.map((d) => [d, ""])));
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRemarks("");
    setCounts(Object.fromEntries(DENOMINATIONS.map((d) => [d, ""])));
    (async () => {
      try {
        const { data } = await api.get("/cash/reconcile/preview");
        setPreview(data);
      } catch (e) { toast.error(apiError(e)); setPreview(null); }
    })();
  }, [open]);

  const physical = useMemo(
    () => DENOMINATIONS.reduce((s, d) => s + d * (+counts[d] || 0), 0),
    [counts],
  );
  const discrepancy = preview ? physical - preview.system_cash_balance : 0;

  const submit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post("/cash/reconcile", {
        date: preview.date,
        physical_cash_count: physical,
        denomination_breakdown: Object.fromEntries(
          DENOMINATIONS.map((d) => [String(d), +counts[d] || 0]).filter(([, n]) => n > 0),
        ),
        remarks: remarks || undefined,
      });
      if (data.status === "MATCHED") toast.success("Cash matched — day closed");
      else toast.warning(`${data.status}: difference ${fmtMoney(data.discrepancy_amount)} — pending accountant review`);
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cash-closing-dialog" className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Daily Cash Closing</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">One closing for the shared cash pot (all warehouses).</p>
        {!preview ? (
          <div className="grid place-items-center py-8"><Loader2 className="animate-spin text-emerald-700" /></div>
        ) : (
          <>
            <div className="space-y-1 rounded-lg bg-stone-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Date</span><span>{preview.date}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Opening</span><span>{fmtMoney(preview.opening_cash)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">+ Receipts</span><span className="text-emerald-700">{fmtMoney(preview.total_receipts)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">− Paid out</span><span className="text-rose-700">{fmtMoney(preview.total_paid_out)}</span></div>
              <div className="flex justify-between border-t border-stone-200 pt-1 font-bold">
                <span>System balance</span><span data-testid="closing-system">{fmtMoney(preview.system_cash_balance)}</span>
              </div>
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Physical denomination count</div>
              <div className="grid grid-cols-3 gap-2">
                {DENOMINATIONS.map((d) => (
                  <label key={d} className="flex items-center gap-2 rounded-lg border border-stone-200 px-2 py-1.5 text-sm">
                    <span className="w-12 font-mono text-xs text-stone-500">₹{d}</span>
                    <span className="text-stone-400">×</span>
                    <NumberInput
                      className="!py-1"
                      value={counts[d]}
                      onChange={(e) => setCounts({ ...counts, [d]: e.target.value })}
                      data-testid={`denom-${d}`}
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-stone-200 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Physical count</span><span className="font-semibold" data-testid="closing-physical">{fmtMoney(physical)}</span></div>
              <div className="flex justify-between items-center">
                <span>Discrepancy</span>
                <span className={`font-bold ${discrepancy === 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid="closing-diff">
                  {fmtMoney(discrepancy)}
                  {discrepancy !== 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium">
                      <AlertTriangle size={12} /> {discrepancy < 0 ? "SHORTAGE" : "OVERAGE"}
                    </span>
                  )}
                </span>
              </div>
            </div>

            <Field label="Remarks">
              <TextInput value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </Field>

            <button onClick={submit} disabled={busy} data-testid="submit-closing-btn" className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />} Submit Daily Closing
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
